import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Hourglass, Play, Undo2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { useTaskHolderName } from './TaskHolder';
import { Button, useToast } from './ui';

/**
 * Who finishes a task and who marks it done (operator, 2026-10-11: "the person
 * who create or assign the task he make it as done assign employees just change
 * status to finish to go to next employee or waiting for review from task
 * creator"). Whoever has it starts it and finishes it: it waits in Review for
 * whoever created it or gave it to them — or, a workflow's step with a level
 * after it, goes on to that level. They mark it done or send it back to work.
 * A task its own creator finishes is done there and then (2026-10-28): nobody
 * else reviews it. The API sends what this person may do in `can` (holds,
 * reviews, finish, handsOn, moves); a Leaders Task keeps its own buttons
 * (LeaderTaskPanels).
 */

/**
 * Whether Done takes the work on to the next level of its workflow, rather than
 * marking their own work done. An API of before 2026-10-28 sends no `handsOn`:
 * Done from Finish was a workflow's then.
 */
function handsOnOf(task: any): boolean {
  return task.can?.handsOn ?? !!task.workflowRunId;
}

/** What the toast says once the move is made. */
function movedText(from: string, to: string, handsOn: boolean): TranslationKey {
  if (to === 'DONE') {
    if (from === 'REVIEW') return 'tasks.markedDone';
    return handsOn ? 'tasks.finishedHandedOn' : 'tasks.finishedOwn';
  }
  if (to === 'REVIEW') return 'tasks.finishedReview';
  return from === 'REVIEW' ? 'tasks.sentBack' : 'tasks.started';
}

function useTaskMove(task: any) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: string) => api.post(`/tasks/${task.id}/status`, { status }),
    onSuccess: (_data, status) => {
      for (const queryKey of [['task', task.id], ['tasks'], ['kanban'], ['dashboard']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      toast.success(t(movedText(task.status, status, handsOnOf(task))));
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** The page header's Start and Finish, for whoever has the task. */
export function TaskWorkActions({ task }: { task: any }) {
  const { t } = useI18n();
  const may = task.can ?? {};
  const move = useTaskMove(task);
  const starts = !!may.holds && task.status === 'TO_DO' && (may.moves ?? []).includes('IN_PROGRESS');
  const finish: 'REVIEW' | 'DONE' | null = may.finish ?? null;
  return (
    <>
      {starts && (
        <Button
          variant="primary"
          icon={<Play className="h-4 w-4 flip-rtl" />}
          loading={move.isPending}
          onClick={() => move.mutate('IN_PROGRESS')}
        >
          {t('tasks.start')}
        </Button>
      )}
      {finish && (
        <Button
          variant="primary"
          icon={<CheckCheck className="h-4 w-4" />}
          loading={move.isPending}
          title={t(
            finish === 'REVIEW'
              ? 'tasks.finishReviewHint'
              : handsOnOf(task)
                ? 'tasks.finishHandsOnHint'
                : 'tasks.finishOwnHint',
          )}
          onClick={() => move.mutate(finish)}
        >
          {t('tasks.finish')}
        </Button>
      )}
    </>
  );
}

const TONES = {
  success: { box: 'border-success/35 bg-success/8', icon: 'text-success-fg dark:text-success' },
  brand: { box: 'border-primary/30 bg-primary/8', icon: 'text-primary-600 dark:text-primary-400' },
};

/**
 * Finished work, waiting in Review: its reviewer is asked to mark it done or
 * send it back; whoever finished it reads who reviews it.
 */
export function TaskReviewBanner({ task }: { task: any }) {
  const { t, localized } = useI18n();
  const holderName = useTaskHolderName();
  const move = useTaskMove(task);
  if (task.status !== 'REVIEW') return null;
  const may = task.can ?? {};
  const moves: string[] = may.moves ?? [];

  let tone: keyof typeof TONES;
  let title: string;
  let body: string;
  let actions = null;
  if (may.reviews && moves.includes('DONE')) {
    tone = 'success';
    title = t('tasks.review.yoursTitle');
    const holder = holderName(task);
    body = holder ? t('tasks.review.yoursBody', { name: holder }) : t('tasks.review.yoursBodyNobody');
    actions = (
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          icon={<CheckCheck className="h-4 w-4" />}
          loading={move.isPending && move.variables === 'DONE'}
          disabled={move.isPending}
          onClick={() => move.mutate('DONE')}
        >
          {t('tasks.review.markDone')}
        </Button>
        {moves.includes('IN_PROGRESS') && (
          <Button
            icon={<Undo2 className="h-4 w-4 flip-rtl" />}
            loading={move.isPending && move.variables === 'IN_PROGRESS'}
            disabled={move.isPending}
            onClick={() => move.mutate('IN_PROGRESS')}
          >
            {t('tasks.review.sendBack')}
          </Button>
        )}
      </div>
    );
  } else if (may.holds) {
    tone = 'brand';
    title = t('tasks.review.waitingTitle');
    const creator = task.createdBy ? localized(task.createdBy) : null;
    const assigner = task.assignedBy && task.assignedBy.id !== task.createdBy?.id ? localized(task.assignedBy) : null;
    body =
      creator && assigner
        ? t('tasks.review.waitingTwo', { creator, assigner })
        : t('tasks.review.waitingOne', { name: creator ?? assigner ?? '—' });
  } else {
    return null;
  }

  return (
    <div className={`mb-5 flex flex-wrap items-center gap-4 rounded-lg border p-4 ${TONES[tone].box}`}>
      <Hourglass className={`h-6 w-6 shrink-0 ${TONES[tone].icon}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-title text-[var(--text-primary)]">{title}</p>
        <p className="mt-0.5 text-body-sm text-muted">{body}</p>
      </div>
      {actions}
    </div>
  );
}
